import { answerQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { IdentityTypes } from "@/interfaces/enums";
import { AnswerQuestionModel, SurveyQuestionUISchema } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { helpCenterLocaleHost } from "@/utils/util";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectOptions, { Option } from "@/components/SelectOptions";
import ContentContainer from "@/containers/common/ContentContainer";

interface DocumentTypeProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	questionFieldToUiSchemaMap: Map<string, string>;
	ocrResult?: number;
	ocrIdentity?: number;
	onNextClick: (val: number) => void;
}

const SORT_KEY: { [index: string]: number } = {
	passport: 1,
	"id-card": 2,
	"alien-id-card": 3,
};
export default function DocumentType(props: DocumentTypeProps) {
	const { locale } = useRouter();
	const { t } = useTranslation("survey");
	const [loading, setLoading] = useState(false);
	const [selectedType, setSelectedType] = useState<string>();
	const [documentTypeOptions, setDocumentTypeOptions] = useState<Option[]>([]);

	useEffect(() => {
		const _ocrIdentity = (props.ocrIdentity && String(props.ocrIdentity)) || "";
		const _answerType = props.answerFieldToValueMap.get("documentType") || "";
		const _documentType = _answerType || _ocrIdentity;
		if (!_documentType) return;

		setSelectedType(_documentType);
	}, [props.answerFieldToValueMap, props.ocrIdentity]);

	useEffect(() => {
		try {
			const uiSchema: SurveyQuestionUISchema = JSON.parse(props.questionFieldToUiSchemaMap.get("documentType") || "");
			const options = uiSchema.options.map((item) => {
				return {
					label: t(item.i18nKey),
					value: item.value,
					key: item.i18nKey,
					id: item.i18nKey,
				};
			});
			const _options = options.sort((pre, next) => {
				return SORT_KEY[pre.key] - SORT_KEY[next.key];
			});

			setDocumentTypeOptions(_options);
		} catch (error) {}
	}, [props.questionFieldToUiSchemaMap, t]);

	const handleConfirm = async (_selectedType: string) => {
		try {
			if (!_selectedType) return;
			const documentTypeQuestionId = props.questionFieldToIdMap.get("documentType");
			if (!documentTypeQuestionId) {
				throw Error("missing documentType questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: _selectedType.toString(),
					questionId: documentTypeQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick(Number(_selectedType));
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);

			log2sensors(SensorsEventIds.AO_UPLOADIDPROOF_NEXTPAGE);
			if (Number(selectedType) === IdentityTypes.PASSPORT) {
				log2sensors(SensorsEventIds.AO_UPLOADPASSPORT1_NEXTPAGE);
			} else if (Number(selectedType) === IdentityTypes.ID_CARD) {
				log2sensors(SensorsEventIds.AO_UPLOADIDCARD1_NEXTPAGE);
			} else {
				log2sensors(SensorsEventIds.AO_UPLOADALIEN1_NEXTPAGE);
			}
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_UPLOADIDPROOF_VIEW);
		if (props.ocrIdentity === IdentityTypes.PASSPORT) {
			log2sensors(SensorsEventIds.AO_UPLOADPASSPORT1_VIEW);
		} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
			log2sensors(SensorsEventIds.AO_UPLOADIDCARD1_VIEW);
		} else if (props.ocrIdentity === IdentityTypes.ALIEN_ID_CARD) {
			log2sensors(SensorsEventIds.AO_UPLOADALIEN1_VIEW);
		} else {
		}
	}, []);

	return (
		<ContentContainer
			animation
			header={
				<div className="px-4.5">
					<SurveyTitle
						title={t("what-typ-of-document-will-you-upload")}
						subtitle={t("this-is-required-to-verify-your-identity")}
						linkTitle={t("acceptable-id-list") + ">"}
						linkUrl={`${helpCenterLocaleHost(locale)}/docs/opening-account/id-requirement/`}
					/>
				</div>
			}
			footer={
				<div className="px-4.5">
					<SurveyFooter
						showSecure={false}
						showOnfido={true}
						nextBtn={false}
						onPrivacyClick={() => {
							log2sensors(SensorsEventIds.AO_UPLOADIDPROOF_PRAVICY);
							if (props.ocrIdentity === IdentityTypes.PASSPORT) {
								log2sensors(SensorsEventIds.AO_UPLOADPASSPORT1_PRAVICY);
							} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADIDCARD1_PRAVICY);
							} else if (props.ocrIdentity === IdentityTypes.ALIEN_ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADALIEN1_PRAVICY);
							} else {
							}
						}}
						onOnfidoClick={() => {
							log2sensors(SensorsEventIds.AO_UPLOADIDPROOF_ONFFIDO);
							if (props.ocrIdentity === IdentityTypes.PASSPORT) {
								log2sensors(SensorsEventIds.AO_UPLOADPASSPORT1_ONFFIDO);
							} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADIDCARD1_ONFFIDO);
							} else if (props.ocrIdentity === IdentityTypes.ALIEN_ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADALIEN1_ONFFIDO);
							} else {
							}
						}}
					/>
				</div>
			}
		>
			<div className="h-12" />
			<SelectOptions
				value={selectedType}
				options={documentTypeOptions}
				loading={loading}
				onChange={(val) => {
					setSelectedType(String(val));
					handleConfirm(String(val));
				}}
			/>
		</ContentContainer>
	);
}
